<template>
  <div style="height: 100%">
    <!-- v4 API -->
    <Line v-if="chartData" :chart-data="chartData" :chart-options="chartOptions" />
  </div>
</template>

<script>
import { computed } from "vue";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale
);

export default {
  name: "HealthLineChart",
  components: { Line },
  props: {
    items: { type: Array, default: () => [] },
  },
  setup(props) {
    const sorted = computed(() =>
      [...(props.items || [])].sort((a, b) =>
        String(a.calendar_date).localeCompare(String(b.calendar_date))
      )
    );

    const chartData = computed(() => {
      const rows = sorted.value;

      // Always return a valid shape, even if empty
      return {
        labels: rows.map((e) => e.calendar_date),
        datasets: [
          {
            label: "Weight (kg)",
            data: rows.map((e) => (e.weight == null ? null : Number(e.weight) / 1000)),
            yAxisID: "yWeight",
            tension: 0.3,
            spanGaps: true,
            borderColor: "#4FC3F7",
            backgroundColor: "rgba(79,195,247,0.15)",
            pointBackgroundColor: "#4FC3F7",
            pointBorderColor: "#4FC3F7",
            borderWidth: 1,
            pointRadius: 0,
          },
          {
            label: "BMI",
            data: rows.map((e) => (e.bmi == null ? null : Number(e.bmi))),
            yAxisID: "yBmi",
            tension: 0.3,
            spanGaps: true,
            borderColor: "#FFB74D",
            backgroundColor: "rgba(255,183,77,0.15)",
            pointBackgroundColor: "#FFB74D",
            pointBorderColor: "#FFB74D",
            borderWidth: 1,
            pointRadius: 0,
          },
          {
            label: "Body Water (%)",
            data: rows.map((e) => (e.body_water == null ? null : Number(e.body_water))),
            yAxisID: "yWater",
            tension: 0.3,
            spanGaps: true,
            borderColor: "#81C784",
            backgroundColor: "rgba(129,199,132,0.15)",
            pointBackgroundColor: "#81C784",
            pointBorderColor: "#81C784",
            borderWidth: 1,
            pointRadius: 0,
          },
        ],
      };
    });

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "top" },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const name = ctx.dataset.label || "";
              const v = ctx.parsed.y;
              if (v == null) return `${name}: -`;
              if (name.includes("Weight")) return `${name}: ${v.toFixed(1)} kg`;
              if (name.includes("BMI")) return `${name}: ${v.toFixed(1)}`;
              if (name.includes("Water")) return `${name}: ${v.toFixed(1)} %`;
              return `${name}: ${v}`;
            },
          },
        },
      },
      scales: {
        x: {
          title: { display: true, text: "Date" },
          ticks: {
            color: "#BDBDBD",
            maxRotation: 45,
            minRotation: 45,
            autoSkip: true,
            maxTicksLimit: 12
          },
        },

        // left axis
        yWeight: {
          type: "linear",
          position: "left",
          title: { display: true, text: "kg" },
        },

        // right axis
        yBmi: {
          type: "linear",
          position: "right",
          title: { display: true, text: "BMI" },
          grid: { drawOnChartArea: false },
        },

        // right axis (offset)
        yWater: {
          type: "linear",
          position: "right",
          offset: true,
          title: { display: true, text: "%" },
          grid: { drawOnChartArea: false },
        },
      },
    };

    return { chartData, chartOptions };
  },
};
</script>
